import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api.js';

// Fetch-on-mount with a manual reload. Deliberately small: this app has one
// user, small payloads, and no need for a caching layer.
export function useApiData(path, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);

  const load = useCallback(
    async (opts = {}) => {
      if (!opts.quiet) setLoading(true);
      try {
        const result = await api.get(path);
        if (mounted.current) {
          setData(result);
          setError(null);
        }
      } catch (err) {
        if (mounted.current) setError(err);
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [path],
  );

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, ...deps]);

  return { data, error, loading, reload: load, setData };
}

// Countdown/stopwatch for rest periods between sets.
export function useTimer() {
  const [seconds, setSeconds] = useState(null);
  const ref = useRef(null);

  const stop = useCallback(() => {
    if (ref.current) clearInterval(ref.current);
    ref.current = null;
    setSeconds(null);
  }, []);

  const start = useCallback(
    (from) => {
      if (ref.current) clearInterval(ref.current);
      setSeconds(from);
      ref.current = setInterval(() => {
        setSeconds((s) => {
          if (s == null || s <= 1) {
            clearInterval(ref.current);
            ref.current = null;
            if (navigator.vibrate) {
              try { navigator.vibrate(120); } catch { /* unsupported */ }
            }
            return null;
          }
          return s - 1;
        });
      }, 1000);
    },
    [],
  );

  useEffect(() => () => ref.current && clearInterval(ref.current), []);

  return { seconds, start, stop, running: seconds != null };
}
